import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { BlueprintUser, BlueprintAuthState } from './types';
import type { BlueprintRole } from '@mn/shared';
import { apiClient, setAuthSession, clearAuthSession, ApiError } from './api';
import { queryClient } from './query-client';
import type { OAuthConfig } from './social-oauth';
import { loginWithGoogle, loginWithApple, loginWithFacebook } from './social-oauth';
import { loginWithMicrosoft as msalLogin, logoutMicrosoft } from './msal-auth';

const AuthContext = createContext<BlueprintAuthState>({
  user: null,
  blueprintRoles: [],
  highestBlueprintRole: null,
  isLoading: true,
  isAuthenticated: false,
  hasBlueprintAccess: false,
  error: null,
  oauthConfig: null,
  loginWithPassword: async () => {},
  loginWithMicrosoft: async () => {},
  loginWithSocial: async () => {},
  logout: async () => {},
  applyUserPatch: () => {},
});

interface MeResponse {
  data: {
    id: string;
    email: string;
    displayName: string;
    blueprintRoles: BlueprintRole[];
    highestBlueprintRole: BlueprintRole | null;
    showGlossaryTooltips: boolean;
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<BlueprintUser | null>(null);
  const [blueprintRoles, setBlueprintRoles] = useState<BlueprintRole[]>([]);
  const [highestBlueprintRole, setHighestBlueprintRole] = useState<BlueprintRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [oauthConfig, setOAuthConfig] = useState<OAuthConfig | null>(null);

  const fetchMe = useCallback(async (): Promise<boolean> => {
    try {
      const res = await apiClient.get<MeResponse>('/me');
      const u = res.data;
      const bpUser: BlueprintUser = {
        id: u.id,
        email: u.email,
        name: u.displayName,
        blueprintRoles: u.blueprintRoles ?? [],
        highestBlueprintRole: u.highestBlueprintRole ?? null,
        showGlossaryTooltips: u.showGlossaryTooltips ?? true,
      };
      setUser(bpUser);
      setBlueprintRoles(bpUser.blueprintRoles);
      setHighestBlueprintRole(bpUser.highestBlueprintRole);
      setError(null);
      return true;
    } catch (err) {
      setUser(null);
      setBlueprintRoles([]);
      setHighestBlueprintRole(null);
      // 401 is the expected "not signed in" case; anything else is a real error.
      if (!(err instanceof ApiError) || err.status !== 401) {
        setError('Authentication failed');
      }
      return false;
    }
  }, []);

  useEffect(() => {
    void (async () => {
      // Load provider config for the login page; ignore failures (login still
      // works for password if the endpoint is unreachable).
      apiClient
        .get<{ data: OAuthConfig }>('/public/oauth-config')
        .then((res) => setOAuthConfig(res.data))
        .catch(() => {});
      await fetchMe();
      setIsLoading(false);
    })();
  }, [fetchMe]);

  const loginWithPassword = useCallback(async (email: string, password: string) => {
    await apiClient.post('/public/auth/login', { email, password });
    setAuthSession({ provider: 'password' });
    await fetchMe();
  }, [fetchMe]);

  const loginWithMicrosoft = useCallback(async () => {
    if (!oauthConfig?.microsoft?.enabled) {
      throw new Error('Microsoft sign-in is not available');
    }
    const { token, email, name } = await msalLogin(
      oauthConfig.microsoft.clientId,
      oauthConfig.microsoft.tenantId,
    );
    setAuthSession({ provider: 'microsoft', token, email, name });
    await fetchMe();
  }, [oauthConfig, fetchMe]);

  const loginWithSocial = useCallback(
    async (provider: 'google' | 'apple' | 'facebook') => {
      const clientId = oauthConfig?.[provider]?.clientId;
      if (!oauthConfig?.[provider]?.enabled || !clientId) {
        throw new Error(`${provider} sign-in is not available`);
      }
      const fn =
        provider === 'google' ? loginWithGoogle : provider === 'apple' ? loginWithApple : loginWithFacebook;
      const result = await fn(clientId);
      setAuthSession({
        provider,
        token: result.token,
        email: result.email,
        name: result.name,
      });
      await fetchMe();
    },
    [oauthConfig, fetchMe],
  );

  const applyUserPatch = useCallback((patch: Partial<BlueprintUser>) => {
    setUser((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // best-effort
    }
    await logoutMicrosoft();
    clearAuthSession();
    /*
     * EVERY CACHED QUERY, NOT JUST THE SESSION. Until the notification bell there was no
     * per-USER resource in the cache — everything was role- or church-scoped and therefore
     * identical for whoever signed in next. `queryKeys.notifications.list()` carries no user
     * identity and the client's default staleTime is five minutes, so on a shared office
     * machine the next person to sign in within that window would have been served the
     * previous person's notifications, from cache, with no refetch.
     */
    queryClient.clear();
    setUser(null);
    setBlueprintRoles([]);
    setHighestBlueprintRole(null);
  }, []);

  const isAuthenticated = user !== null;
  const hasBlueprintAccess = blueprintRoles.length > 0;

  return (
    <AuthContext.Provider
      value={{
        user,
        blueprintRoles,
        highestBlueprintRole,
        isLoading,
        isAuthenticated,
        hasBlueprintAccess,
        error,
        oauthConfig,
        loginWithPassword,
        loginWithMicrosoft,
        loginWithSocial,
        logout,
        applyUserPatch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): BlueprintAuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
